import moment, { Moment } from 'moment';

import { covertFormatGroup, percentFormatGroup } from '../../shared/common/constants/groups';

/**
 * «Динамика» — сравнение метрик отчёта с предыдущим периодом.
 *
 * Правила «рост — хорошо или плохо» согласованы с заказчиком 10.09.2026
 * (файл «Анализ периода - правила стрелок.xlsx»). Этими же правилами красится
 * режим «Сравнить» (formatCompareData в helpers/utils/reports.tsx), чтобы одна
 * метрика не была зелёной в одном месте и красной в другом.
 */

/** Как читать рост метрики: up — рост хорошо, down — рост плохо, neutral — ни то ни другое */
export type MetricTrendDirection = 'up' | 'down' | 'neutral';

/** Итоговый цвет изменения: good — зелёный, bad — красный, neutral/same — синий */
export type MetricTrendTone = 'good' | 'bad' | 'neutral' | 'same';

/** Рост — плохо: стрелка вверх красная, вниз зелёная. Цены (*_price) добавляются правилом ниже */
const LOWER_IS_BETTER_METRICS = new Set<string>([
    // Возвраты
    'refunds_count',
    'refunds_sum',
    'buyers_with_refund_count',
    'user_to_refunder_converse',
    'buyer_to_refunder_converse',
    // Комиссия платёжных систем — расход
    'payments_commission_sum',
    // Недоплаченные и неоплаченные заказы
    'porders_count',
    'porders_sum',
    'worders_count',
    'worders_sum',
    // Рано ушли с вебинара
    'web_1min_users_count',
    'web_5min_users_count',
    'web_15min_users_count',
    'web_30min_users_count',
    'web_60min_users_count',
    'web_1min_users_part',
    'web_5min_users_part',
    'web_15min_users_part',
    'web_30min_users_part',
    'web_60min_users_part',
    // Стоимость рекламы
    'cpm',
    'cpc',
    'drr',
]);

/** Нейтральные: рост и падение синие — это объём вложений, а не результат */
const NEUTRAL_METRICS = new Set<string>([
    'ads_spend',
    'ads_spend_cabinet',
]);

/** Метрики в процентах, у которых нет суффикса _converse / _part */
const PERCENT_METRICS = new Set<string>(
    [ ...percentFormatGroup, ...covertFormatGroup ].filter((key) => key !== 'orders_sum')
);

/** Метаданные метрики школы (кастомной), по которым можно понять её смысл */
export interface TrendMetricMeta {
    base_metric_name?: string;
    metric_formula?: string;
    unit?: string;
}

/**
 * Ключ метрики без служебных суффиксов:
 * '__cmp' (второй период «Сравнить»), '_cumulative' (накопительный график),
 * '_2'…'_9' (отчёты по заказам, платежам, вебинарам, посещениям).
 * 'orders_count_2_cumulative' → 'orders_count'
 */
export const toBaseReportMetricKey = (dataKey: string): string =>
    String(dataKey ?? '')
        .replace(/__cmp$/, '')
        .replace(/_cumulative$/, '')
        .replace(/_[2-9]$/, '');

const getStandardMetricDirection = (baseKey: string): MetricTrendDirection => {
    if (!baseKey || baseKey.startsWith('_')) return 'neutral';
    if (NEUTRAL_METRICS.has(baseKey)) return 'neutral';
    if (LOWER_IS_BETTER_METRICS.has(baseKey) || baseKey.endsWith('_price')) return 'down';
    return 'up';
};

/**
 * Базовая метрика для метрики школы: из base_metric_name или из формулы,
 * если в формуле ровно одна метрика (остальное — числа). Иначе смысл не понять — null.
 */
const resolveCustomBaseMetric = (meta?: TrendMetricMeta): string | null => {
    if (meta?.base_metric_name) {
        return toBaseReportMetricKey(meta.base_metric_name);
    }
    if (meta?.metric_formula) {
        const [ operation, ...args ] = meta.metric_formula.split('/');
        if (operation === 'calculate_conversion') return null;
        const metricArgs = args.filter((part) => part && isNaN(Number(part)));
        if (metricArgs.length === 1) {
            return toBaseReportMetricKey(metricArgs[0]);
        }
    }
    return null;
};

/** Рост метрики — хорошо, плохо или нейтрально */
export const getMetricTrendDirection = (dataKey: string, meta?: TrendMetricMeta): MetricTrendDirection => {
    if (!dataKey) return 'neutral';
    if (dataKey.startsWith('_')) {
        const baseKey = resolveCustomBaseMetric(meta);
        return baseKey ? getStandardMetricDirection(baseKey) : 'neutral';
    }
    return getStandardMetricDirection(toBaseReportMetricKey(dataKey));
};

/** Метрика показывается в процентах — изменение считаем в процентных пунктах */
export const isPercentMetric = (dataKey: string, meta?: TrendMetricMeta): boolean => {
    if (!dataKey) return false;
    if (dataKey.startsWith('_')) {
        if (meta?.unit === '%' || meta?.unit === 'Percentage') return true;
        return (meta?.metric_formula ?? '').split('/')[0] === 'calculate_conversion';
    }
    const baseKey = toBaseReportMetricKey(dataKey);
    return baseKey.endsWith('_converse') || baseKey.endsWith('_part') || PERCENT_METRICS.has(baseKey);
};

export interface AnalysisPeriodInput {
    start: Moment | string;
    end: Moment | string;
    interval?: string;
    withTime?: boolean;
}

export interface AnalysisPeriod {
    start: Moment;
    end: Moment;
}

/**
 * Период, с которым сравниваем:
 * - этот/прошлый месяц, квартал, год — весь предыдущий месяц, квартал, год
 *   (решение заказчика: даже если текущий ещё не закончился);
 * - эта/прошлая неделя — те же дни неделей раньше;
 * - период со временем (N часов назад) — столько же времени сразу перед;
 * - остальное (сегодня, вчера, N дней, свои даты) — столько же дней сразу перед.
 * «За всё время» сравнивать не с чем — null.
 */
export const getPreviousPeriod = (period?: AnalysisPeriodInput | null): AnalysisPeriod | null => {
    if (!period?.start || !period?.end) return null;
    const start = moment(period.start);
    const end = moment(period.end);
    if (!start.isValid() || !end.isValid() || end.isBefore(start)) return null;

    switch (period.interval) {
        case 'allthetime':
            return null;
        case 'thismonth':
        case 'lastmonth': {
            const prevStart = start.clone().subtract(1, 'month').startOf('month');
            return { start: prevStart, end: prevStart.clone().endOf('month') };
        }
        case 'thisquart':
        case 'lastquart': {
            const prevStart = start.clone().subtract(1, 'quarter').startOf('quarter');
            return { start: prevStart, end: prevStart.clone().endOf('quarter') };
        }
        case 'thisyear':
        case 'lastyear': {
            const prevStart = start.clone().subtract(1, 'year').startOf('year');
            return { start: prevStart, end: prevStart.clone().endOf('year') };
        }
        case 'thisweek':
        case 'lastweek':
            return { start: start.clone().subtract(7, 'days'), end: end.clone().subtract(7, 'days') };
    }

    if (period.withTime) {
        const durationMs = end.diff(start);
        const prevEnd = start.clone().subtract(1, 'second');
        return { start: prevEnd.clone().subtract(durationMs, 'milliseconds'), end: prevEnd };
    }

    const days = end.clone().startOf('day').diff(start.clone().startOf('day'), 'days') + 1;
    const prevEnd = start.clone().subtract(1, 'day').endOf('day');
    const prevStart = prevEnd.clone().subtract(days - 1, 'days').startOf('day');
    return { start: prevStart, end: prevEnd };
};

/** «01.08.2026 – 31.08.2026» (один день — одна дата) */
export const formatAnalysisPeriod = (period: AnalysisPeriod, withTime?: boolean): string => {
    const format = withTime ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY';
    const start = period.start.format(format);
    const end = period.end.format(format);
    return start === end ? start : `${start} – ${end}`;
};

/**
 * Число из ячейки. Пусто, '-' и отсутствующее значение — это 0.
 * Строки с «%», «=», « +», « -» — уже преобразованные значения
 * (режим «% от итого», «Сравнить»), сравнивать их нельзя — null.
 */
export const parseMetricValue = (value: unknown): number | null => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value !== 'string') return null;
    const text = value.replace(/&nbsp;/g, ' ').trim();
    if (text === '' || text === '-') return 0;
    if (/[%=]| [+-]/.test(text)) return null;
    const parsed = Number(text.replace(/\s+/g, '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
};

export interface MetricChange {
    /** Изменение: в процентах или в процентных пунктах */
    delta: number;
    unit: 'percent' | 'points';
}

/**
 * (Сейчас − Было) / |Было| × 100. Для метрик в процентах — разница в пунктах.
 * Было 0, стало не 0 — ±100% (решение заказчика).
 */
export const calcMetricChange = (current: number, previous: number, isPercent: boolean): MetricChange => {
    if (isPercent) {
        return { delta: current - previous, unit: 'points' };
    }
    if (previous === 0) {
        return { delta: current === 0 ? 0 : (current > 0 ? 100 : -100), unit: 'percent' };
    }
    return { delta: ((current - previous) / Math.abs(previous)) * 100, unit: 'percent' };
};

/** Цвет изменения с учётом смысла метрики */
export const getTrendTone = (direction: MetricTrendDirection, delta: number): MetricTrendTone => {
    if (delta === 0) return 'same';
    if (direction === 'neutral') return 'neutral';
    const isGrowth = delta > 0;
    return (direction === 'up') === isGrowth ? 'good' : 'bad';
};

/**
 * Цвет спарклайна: последняя точка к первой по тем же правилам, что у «Динамики».
 * Пустая серия или одна точка — «без изменений».
 */
export const getSeriesTone = (values: number[], dataKey: string, meta?: TrendMetricMeta): MetricTrendTone => {
    const series = values.filter((v) => Number.isFinite(v));
    if (series.length < 2) return 'same';
    return getTrendTone(getMetricTrendDirection(dataKey, meta), series[series.length - 1] - series[0]);
};

/**
 * «+33.3%», «−10.0 п.п.», «= 0%»: направление — знаком, без треугольников.
 * Один знак после запятой (десятые): сотые Денис просил убрать, они только шумят.
 * Совсем малое изменение не прячем: «+<0.1%»
 */
export const formatMetricChange = (change: MetricChange): string => {
    const unit = change.unit === 'points' ? ' п.п.' : '%';
    if (change.delta === 0) return `= 0${unit}`;
    const abs = Math.abs(change.delta);
    const value = abs < 0.05 ? '<0.1' : abs.toFixed(1);
    return `${change.delta > 0 ? '+' : '−'}${value}${unit}`;
};

/**
 * Ключ строки для сопоставления периодов: путь id группировок ('youtube.com-12').
 * В плоском режиме id есть только у первого уровня, поэтому добавляем подпись строки.
 */
export const buildAnalysisRowKey = (path: unknown[], group: unknown, flatMode: boolean): string => {
    const pathKey = path.join('-');
    // В отчёте по платежам подписи приходят с &nbsp;, а в таблице уже с пробелами (prepareRows)
    return flatMode ? `${pathKey}|${String(group ?? '').replace(/&nbsp;/g, ' ')}` : pathKey;
};
